import React from 'react'
import SlerpPayLogo from '@slerp/assets/slerp-pay.svg'
import StripeLogo from '@slerp/assets/stripe-logo.svg'
import { PageHeader, Button, Space, Tooltip } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { ImageFlexBox, LogoImage } from './StyledComponents'
import styled from '@emotion/styled'
import { StripeConnectData } from '@slerp/controls'

type StripeUrlType = 'slerp_pay' | 'default'

interface StripeConnectBadgeProps {
  pendingOnboarding: boolean
  type?: StripeUrlType
  stripeId?: string
  url: string
  loading: boolean
  hasConnected: boolean
  loginUrl: string
  hasStripeCustomerId: boolean
  onConnect?: () => void
  isAllowed?: boolean
}

const StripeConnectBadge = ({
  pendingOnboarding,
  type,
  stripeId,
  url,
  loading,
  hasConnected,
  loginUrl,
  hasStripeCustomerId,
  onConnect,
  isAllowed = true
}: StripeConnectBadgeProps) => {
  const displayMessage = hasConnected
    ? `Your ${
        type === 'slerp_pay' ? 'Slerp Pay' : 'Stripe'
      } account is connected successfully.`
    : `Connect your stripe account to receive ${
        type === 'slerp_pay' ? 'customer payments' : 'your funds'
      }.`
  const unableToTransact = `Your ${
    type === 'slerp_pay' ? 'Slerp Pay' : 'Stripe'
  } account is connected, but currently unable to transact`

  const handleConnect = () => {
    if (onConnect) {
      onConnect()
    }
  }

  const creditCardText =
    'In order to connect to Stripe you will need to add your Credit Card details first'
  const unauthorizedText =
    'In order to connect to Stripe you should have sufficient rights'

  const unauthorizedDashboardText =
    'In order to use Stripe Dashboard you should have sufficient rights'

  return (
    <StripeCard pendingOnboarding={pendingOnboarding}>
      <span className='container'>
        <Subtext>
          <span>{pendingOnboarding ? unableToTransact : displayMessage}</span>
        </Subtext>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <Space>
            {hasConnected && (
              <Tooltip
                title={unauthorizedDashboardText}
                color='#d092dd'
                overlayStyle={{
                  visibility: `${isAllowed ? 'hidden' : 'visible'}`
                }}
              >
                <Button
                  type='primary'
                  disabled={!isAllowed}
                  onClick={loginUrl ? undefined : handleConnect}
                >
                  <a
                    type='primary'
                    href={loginUrl || url}
                    target='_blank'
                    rel='noreferrer'
                    className='primary'
                  >
                    <ButtonText>VIEW DASHBOARD</ButtonText>
                  </a>
                </Button>
              </Tooltip>
            )}
            {!hasConnected && (
              <Tooltip
                title={isAllowed ? creditCardText : unauthorizedText}
                color='#d092dd'
                overlayStyle={{
                  visibility: `${
                    hasStripeCustomerId && isAllowed ? 'hidden' : 'visible'
                  }`
                }}
              >
                <Button
                  type={loginUrl ? 'default' : 'primary'}
                  href={url}
                  onClick={handleConnect}
                  disabled={hasStripeCustomerId === false}
                >
                  <ButtonText style={{ paddingTop: '4px' }}>CONNECT</ButtonText>
                </Button>
              </Tooltip>
            )}
          </Space>
        )}
      </span>

      <ImageFlexBox>
        {type === 'slerp_pay' ? (
          <LogoImage
            className='icon'
            data-testid='slerp-pay-logo'
            src={SlerpPayLogo}
            alt='Slerp Pay Logo'
          />
        ) : (
          <LogoImage
            className='icon'
            data-testid='stripe-logo'
            src={StripeLogo}
            alt='Stripe Logo'
          />
        )}

        {hasConnected && (
          <ConnectedId>Connected Account ID: {stripeId}</ConnectedId>
        )}
      </ImageFlexBox>
    </StripeCard>
  )
}

interface StripeConnectProps {
  loading?: boolean
  stripeId?: string
  isAllowed?: boolean
  hasStripeCustomerId: boolean
  stripeConnectUrl: StripeConnectData | undefined | null
  onConnect?: () => void
}

export const StripeConnect = ({
  stripeConnectUrl,
  stripeId,
  loading = false,
  hasStripeCustomerId,
  onConnect,
  isAllowed
}: StripeConnectProps) => {
  if (!stripeConnectUrl) {
    return <></>
  }

  const pendingOnboarding =
    stripeConnectUrl.hasConnected && !stripeConnectUrl.hasOnboarded

  return (
    <>
      <PageHeader
        title={
          <>
            Payment Solution
            <Tooltip
              title={
                <span>
                  Visit Stripe's dashboard{' '}
                  <a
                    href='https://dashboard.stripe.com/login'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    here
                  </a>
                  .
                </span>
              }
            >
              <QuestionCircleOutlined className='_ml-8' />
            </Tooltip>
          </>
        }
      />

      {pendingOnboarding && (
        <WarningBanner>
          <WarningIcon>
            <span>1</span>
          </WarningIcon>
          <WarningText>
            Please visit your Stripe Dashboard to finish your onboarding
          </WarningText>
        </WarningBanner>
      )}
      <StripeConnectBadge
        pendingOnboarding={pendingOnboarding}
        url={stripeConnectUrl.url}
        stripeId={stripeId}
        type={stripeConnectUrl?.type}
        hasConnected={stripeConnectUrl.hasConnected}
        loginUrl={stripeConnectUrl.login_link}
        loading={loading}
        hasStripeCustomerId={hasStripeCustomerId}
        onConnect={onConnect}
        isAllowed={isAllowed}
      />
    </>
  )
}

const WarningBanner = styled.div`
  background-color: #fff;
  border: 1px solid #a40000;
  border-radius: 4px 4px 0 0;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`

const WarningIcon = styled.div`
  background-color: #a40000;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 10px;
`

const WarningText = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: #a40000;
  text-decoration: underline;
  margin: 0;
`

const StripeCard = styled.div(
  ({ pendingOnboarding }: { pendingOnboarding: boolean }) => `
  width: 100%;
  box-shadow: inset 0 0 0 1px #8883;
  padding: 16px;
  border-radius: ${pendingOnboarding ? '0 0 4px 4px' : '4px'};
  margin-bottom: 8px;
  min-height: 96px;
  background-color: #4a4a68;
  font-size: 12px;
  color: #fff;
  display: flex;
  justify-content: space-between;
  > .icon {
    width: 100px;
    margin-top: -20px;
  }

  .container {
    background-color: inherit;
  }
`
)

const ButtonText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #fff;
`

const Subtext = styled.div`
  padding-bottom: 15px;
  font-size: 16px;
  font-weight: 500;
  color: #fff;
`

const ConnectedId = styled.div`
  padding-bottom: 15px;
`
